// ============================================================
// Form SPK — selaras spk_form.dart (Flutter Stepper 5 langkah)
// ============================================================

import Alpine from 'alpinejs';

const STEP_LABELS = ['Pelanggan', 'Tanggal', 'Produk', 'Detail Produksi', 'Simpan'];

Alpine.data('spkForm', (config = {}) => ({
  currentStep: 0,
  stepLabels: STEP_LABELS,
  isSaving: false,
  formErrors: {},
  dataLoading: false,
  printerReady: typeof window.print === 'function',

  customerSearch: '',
  showCustomerDropdown: false,
  productDropdownIdx: -1,

  customers: [],
  products: [],

  form: {
    customer_name: '',
    customer_phone: '',
    customer_id: null,
    customer_email: '',
    tgl_masuk: '',
    due_date: '',
    items: [{ nama: '', ukuran: '', jumlah: 1, keterangan: '' }],
    process_type: '',
    finishing: '',
    lamination: '',
    notes: '',
    designer_name: '',
    printer_name: '',
    file_name: '',
  },

  processOptions:    config.processOptions    || [],
  finishingOptions:  config.finishingOptions  || [],
  laminationOptions: config.laminationOptions || [],

  apiUrl:       config.apiUrl       || '/api/v1/kasir/spk',
  productsUrl:  config.productsUrl  || '/api/v1/uspati/v4/products',
  customersUrl: config.customersUrl || '/api/v1/uspati/v4/customers',
  hubUrl:       config.hubUrl       || '/ops/k9x/spk',

  async init() {
    await this.loadMasterData();
  },

  async loadMasterData() {
    this.dataLoading = true;
    try {
      const [prodRes, custRes] = await Promise.all([
        axios.get(this.productsUrl),
        axios.get(this.customersUrl),
      ]);
      this.products  = Array.isArray(prodRes.data) ? prodRes.data : (prodRes.data?.data || []);
      this.customers = Array.isArray(custRes.data) ? custRes.data : (custRes.data?.data || []);
    } catch (e) {
      console.error('SPK load master data:', e);
      window.toast?.('Gagal memuat data produk/pelanggan.', 'warning');
    } finally {
      this.dataLoading = false;
    }
  },

  get filteredCustomers() {
    const q = (this.customerSearch || '').trim().toLowerCase();
    if (!q) return [];
    return this.customers.filter((c) =>
      (c.nama || '').toLowerCase().includes(q)
      || (c.kontak || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || String(c.id || '').toLowerCase().includes(q)
    ).slice(0, 15);
  },

  get selectedCustomerLabel() {
    if (!this.form.customer_id) return '';
    const parts = [this.form.customer_name || '', this.form.customer_phone || ''].filter(Boolean);
    return parts.join(' • ');
  },

  productSuggestions(query) {
    const q = (query || '').trim().toLowerCase();
    if (!q) return this.products.slice(0, 30);
    return this.products.filter((p) => (p.nama || '').toLowerCase().includes(q)).slice(0, 20);
  },

  onCustomerSearch() {
    const query = (this.customerSearch || '').trim();
    const digits = query.replace(/\D/g, '');
    const looksLikePhone = digits.length >= 4 && /^[\d+\s()-]+$/.test(query);

    if (this.form.customer_id && query !== this.selectedCustomerLabel) {
      this.form.customer_id = null;
      this.form.customer_email = '';
    }

    if (looksLikePhone) {
      this.form.customer_phone = query;
      if (!this.form.customer_name?.trim()) {
        this.form.customer_name = '';
      }
    } else if (query && !this.form.customer_id) {
      this.form.customer_name = query;
    }

    this.showCustomerDropdown = this.filteredCustomers.length > 0;
  },

  selectMember(c) {
    this.form.customer_name  = c.nama || '';
    this.form.customer_phone = c.kontak || '';
    this.form.customer_email = c.email || '';
    this.form.customer_id    = c.id || null;
    this.customerSearch      = this.selectedCustomerLabel;
    this.showCustomerDropdown = false;
  },

  clearSelectedCustomer() {
    this.form.customer_id = null;
    this.form.customer_email = '';
    this.customerSearch = '';
    this.showCustomerDropdown = false;
  },

  openProductDropdown(idx) {
    this.productDropdownIdx = idx;
  },

  addProduct() {
    this.form.items.push({ nama: '', ukuran: '', jumlah: 1, keterangan: '' });
  },

  pickProduct(idx, product) {
    this.form.items[idx].nama = product.nama;
    this.productDropdownIdx = -1;
  },

  validateStep(step) {
    this.formErrors = {};

    if (step === 0) {
      const hasName = !!this.form.customer_name?.trim();
      const hasPhone = !!(this.form.customer_phone || '').trim();
      if (!hasName && !hasPhone) {
        this.formErrors.customer_name = 'Isi nama atau nomor HP pelanggan.';
      }
    }

    if (step === 1) {
      if (!this.form.tgl_masuk) this.formErrors.tgl_masuk = 'Pilih tanggal masuk.';
      if (!this.form.due_date) this.formErrors.due_date = 'Pilih tanggal selesai.';
      if (this.form.tgl_masuk && this.form.due_date && this.form.due_date < this.form.tgl_masuk) {
        this.formErrors.due_date = 'Tanggal selesai tidak valid.';
      }
    }

    if (step === 2) {
      const valid = this.form.items.filter((i) => i.nama?.trim());
      if (!valid.length) this.formErrors.items = 'Isi minimal 1 produk.';
    }

    return Object.keys(this.formErrors).length === 0;
  },

  nextStep() {
    if (!this.validateStep(this.currentStep)) return;
    if (this.currentStep < 4) this.currentStep++;
  },

  prevStep() {
    if (this.currentStep > 0) this.currentStep--;
  },

  buildPayload() {
    const items = this.form.items
      .filter((i) => i.nama?.trim())
      .map((i) => ({
        nama: i.nama.trim(),
        ukuran: (i.ukuran || '').trim(),
        jumlah: Math.max(1, parseInt(i.jumlah, 10) || 1),
        keterangan: (i.keterangan || '').trim(),
      }));

    return {
      customer_name:  this.form.customer_name.trim(),
      customer_phone: (this.form.customer_phone || '').trim(),
      customer_email: (this.form.customer_email || '').trim() || null,
      customer_id:    this.form.customer_id || null,
      is_member:      !!this.form.customer_id,
      tgl_masuk:      this.form.tgl_masuk,
      due_date:       this.form.due_date,
      items,
      process_type:   this.form.process_type || '',
      finishing:      this.form.finishing || '',
      lamination:     this.form.lamination || '',
      notes:          (this.form.notes || '').trim(),
      designer_name:  (this.form.designer_name || '').trim(),
      printer_name:   (this.form.printer_name || '').trim(),
      file_name:      (this.form.file_name || '').trim(),
      status:         'confirmed',
    };
  },

  async submit() {
    for (let s = 0; s <= 2; s++) {
      if (!this.validateStep(s)) {
        this.currentStep = s;
        return;
      }
    }

    if (!this.form.tgl_masuk || !this.form.due_date) {
      this.currentStep = 1;
      this.validateStep(1);
      return;
    }

    this.isSaving = true;
    this.formErrors = {};

    try {
      const { data } = await axios.post(this.apiUrl, this.buildPayload());
      if (!data?.status) throw new Error(data?.message || 'Gagal menyimpan SPK.');

      const preview = data.data?.preview_url;
      window.location.href = preview || this.hubUrl;
    } catch (e) {
      const errs = e.response?.data?.errors || {};
      if (errs && typeof errs === 'object') {
        this.formErrors = Object.fromEntries(
          Object.entries(errs).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])
        );
      }
      window.toast?.(e.response?.data?.message || e.message || 'Gagal menyimpan SPK.', 'error');
    } finally {
      this.isSaving = false;
    }
  },

  toastPrinterInfo() {
    window.toast?.('Di web, SPK dicetak via browser setelah simpan (preview otomatis).', 'info');
  },
}));
